import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight, ChevronLeft, Loader2, Image, RefreshCw, ExternalLink, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

interface WizardSiteAdsProps {
  onNext: () => void;
  onPrevious: () => void;
  siteId?: string;
}

interface SiteAd {
  id: string;
  ad_type: string;
  image_url: string;
  is_active: boolean;
}

interface GenerationJob {
  id: string;
  status: string;
  progress: number;
  total_items: number;
  error_message: string | null;
}

const AD_TYPE_LABELS: Record<string, string> = {
  buyer: 'Buyer',
  seller: 'Seller',
  market_value: 'Market Value',
  mortgage: 'Mortgage',
  community_guide: 'Community Guide',
};

export function WizardSiteAds({ onNext, onPrevious, siteId }: WizardSiteAdsProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [ads, setAds] = useState<SiteAd[]>([]);
  const [job, setJob] = useState<GenerationJob | null>(null);

  useEffect(() => {
    if (siteId) {
      fetchAds();
      
      const channel = supabase
        .channel(`site-ads-job-${siteId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'generation_jobs',
            filter: `site_id=eq.${siteId}`,
          },
          () => fetchAds()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [siteId]);

  const fetchAds = async () => {
    if (!siteId) return;

    try {
      const [adsRes, jobRes] = await Promise.all([
        supabase
          .from('site_ads')
          .select('id, ad_type, image_url, is_active')
          .eq('site_id', siteId)
          .order('created_at', { ascending: false }),
        supabase
          .from('generation_jobs')
          .select('id, status, progress, total_items, error_message')
          .eq('site_id', siteId)
          .eq('job_type', 'ads')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      setAds(adsRes.data || []);
      setJob(jobRes.data);
    } catch (error) {
      console.error('Error fetching site ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAllMissing = async () => {
    if (!siteId) return;

    setGenerating(true);
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) throw new Error('Not authenticated');

      const adTypes = ['buyer', 'seller', 'market_value', 'mortgage', 'community_guide'];
      const existingTypes = new Set(ads.map(a => a.ad_type));
      const missingTypes = adTypes.filter(t => !existingTypes.has(t));

      if (missingTypes.length === 0) {
        toast({ title: 'All ad types already exist' });
        setGenerating(false);
        return;
      }

      await Promise.all(
        missingTypes.map(adType =>
          fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-site-ads`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.data.session!.access_token}`,
            },
            body: JSON.stringify({ siteId, adType }),
          })
        )
      );

      toast({ title: 'Site ads generated successfully!' });
      fetchAds();
    } catch (error: any) {
      toast({
        title: 'Error generating ads',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isProcessing = job?.status === 'processing' || job?.status === 'pending';
  const hasAds = ads.length > 0;
  const progressPercent = job && job.total_items > 0 
    ? Math.round((job.progress / job.total_items) * 100) 
    : 0;

  // Group ads by type
  const adsByType = ads.reduce((acc, ad) => {
    if (!acc[ad.ad_type]) acc[ad.ad_type] = [];
    acc[ad.ad_type].push(ad);
    return acc;
  }, {} as Record<string, SiteAd[]>);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Image className="h-5 w-5 text-primary" />
              <CardTitle>Site Ads</CardTitle>
            </div>
            {hasAds && <Badge variant="default">{ads.length} ads</Badge>}
            {isProcessing && <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Generating</Badge>}
          </div>
          <CardDescription>
            Create promotional ads for market value reports, mortgage info, and community guides.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progressPercent} className="h-2" />
              <p className="text-sm text-muted-foreground text-center">
                AI is generating your site ads...
              </p>
            </div>
          )}

          {hasAds ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(adsByType).map(([type, typeAds]) => (
                  <div key={type} className="space-y-2">
                    <h4 className="text-sm font-medium">{AD_TYPE_LABELS[type] || type} ({typeAds.length})</h4>
                    {typeAds[0] && (
                      <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                        <img 
                          src={typeAds[0].image_url} 
                          alt={`${type} ad`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => window.open('/admin/ads', '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Manage All Ads
                </Button>
                <Button variant="outline" onClick={generateAllMissing} disabled={generating || isProcessing}>
                  {generating ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Generate Missing
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                No site ads have been created yet. Generate ads to promote your services across the site.
              </p>
              <Button onClick={generateAllMissing} disabled={generating || isProcessing}>
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Generate Site Ads
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <Button onClick={onNext} disabled={isProcessing}>
          {isProcessing ? 'Please wait...' : 'Continue'}
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
